import React, { useEffect, useMemo, useState } from "react";
import {
  applyTaskProductProposal,
  approveTaskProductDocument,
  createTaskProductProposal,
  createTaskSource,
  getTaskProductDocument,
  patchTaskProductDocument,
  rejectTaskProductProposal,
  undoTaskProductDocument,
  unfreezeTaskProductDocument,
  uploadTaskSourceContent,
} from "./api";
import { taskShortTitle } from "./taskShortTitle";
import { founderLifecycleLabel, proposalPreview } from "./taskHandoffDocument";
import {
  focusTaskSectionId,
  partitionRisks,
  sourceDisplayName,
} from "./taskProductDisplay";
import {
  EmptySectionState,
  EvidenceBadge,
  SourceRow,
  TaskItemRow,
  TaskSectionHeader,
} from "./FullTaskChrome";
import { TASK_SOURCE_ACCEPT } from "./taskSourceCopy";
import "./FullTaskPage.css";

const SECTIONS = [
  { id: "outcome", label: "Outcome" },
  { id: "requirements", label: "What to build" },
  { id: "decisions", label: "Decisions" },
  { id: "exclusions", label: "Boundaries" },
  { id: "successChecks", label: "Success checks" },
  { id: "risks", label: "Risks and deferrals" },
  { id: "sources", label: "What this Task is based on" },
];

function emptyDocument(plan) {
  return {
    revision: 0,
    frozen: false,
    lifecycleState: "draft",
    content: {
      title: taskShortTitle(plan?.featureRequestText || "") || "Untitled Task",
      outcome: plan?.featureRequestText || "",
      outcomeExplanation: "",
      requirements: [],
      decisions: [],
      exclusions: [],
      successChecks: [],
      risks: [],
      sources: [],
    },
  };
}

export default function FullTaskPage({
  plan,
  repo,
  projection,
  mutateAllowed = true,
  discussContext = null,
  onOpenConversation,
  onDocumentChange,
  onDiscuss,
  onApproved,
}) {
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState("");
  const [loadState, setLoadState] = useState(plan?._id ? "loading" : "idle");
  const [saveState, setSaveState] = useState("Saved");
  const [editing, setEditing] = useState(null);
  const [draftText, setDraftText] = useState("");
  const [draftTitle, setDraftTitle] = useState("");
  const [draftExtra, setDraftExtra] = useState("");
  const [draftUsage, setDraftUsage] = useState("context");
  const [confirmHandoff, setConfirmHandoff] = useState(false);
  const [sourceOpen, setSourceOpen] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [pendingRemove, setPendingRemove] = useState(null);
  const sourceFileRef = React.useRef(null);

  const load = async () => {
    if (!plan?._id) return;
    setLoadState("loading");
    try {
      const data = await getTaskProductDocument(plan._id);
      setPayload(data);
      setError("");
      setLoadState("ready");
      onDocumentChange?.(data);
    } catch (err) {
      const raw = err?.response?.data?.error || err?.message || "This Task could not be loaded.";
      const missing = err?.response?.status === 404 || /not found/i.test(String(raw));
      setPayload(null);
      setLoadState("error");
      setError(missing ? "This Task could not be loaded." : raw);
    }
  };

  useEffect(() => {
    void load();
  }, [plan?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loadState !== "ready") return;
    const section = discussContext?.focusSection || discussContext?.section;
    const itemId = discussContext?.itemId;
    if (!section && !itemId) return;
    const node = window.document.getElementById(itemId ? `full-task-item-${itemId}` : focusTaskSectionId(section));
    node?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    if (node) node.setAttribute("data-focused", "true");
  }, [loadState, discussContext?.focusSection, discussContext?.section, discussContext?.itemId]);

  const document = payload?.document || emptyDocument(plan);
  const content = document.content || emptyDocument(plan).content;
  const readiness = payload?.readiness || { ready: false, blockers: [], requirementCount: 0, decisionCount: 0, successCheckCount: 0, openCriticalCount: 0 };
  const frozen = Boolean(document.frozen);
  const proposals = payload?.proposals || [];
  const history = payload?.history || [];
  const materialRequirements = (content.requirements || []).filter((item) => !item.possiblyUnrelated);
  const unrelated = (content.requirements || []).filter((item) => item.possiblyUnrelated);
  const { risks, deferrals } = partitionRisks(content.risks || []);
  const stateLabel = founderLifecycleLabel(document.lifecycleState, projection?.stage);

  const persistPatch = async (patch) => {
    const data = await patchTaskProductDocument(plan._id, {
      baseRevision: document.revision,
      content: patch,
    });
    setPayload(data);
    onDocumentChange?.(data);
    setEditing(null);
    setPendingRemove(null);
    setSaveState("Saved");
    return data;
  };

  const startEdit = (section, item, mode) => {
    if (frozen || !mutateAllowed) return;
    setEditing({ section, id: item?.id || (mode === "add" ? "__new__" : section), mode: mode || (item ? "edit" : "add") });
    setDraftTitle(item?.title || item?.what || item?.name || content.title || "");
    setDraftText(item?.explanation || item?.text || item?.selected || item?.why || item?.contributed || content.outcome || "");
    setDraftExtra(item?.impact || item?.mitigation || item?.revisitWhen || item?.href || "");
    setDraftUsage(item?.usage || (section === "sources" ? "context" : "risk"));
  };

  const saveEdit = async () => {
    if (!plan?._id || !editing) return;
    setSaveState("Saving");
    setError("");
    try {
      const patch = {};
      if (editing.section === "outcome") {
        patch.outcome = draftText;
        if (draftTitle) patch.title = draftTitle;
      } else if (editing.section === "requirements") {
        if (editing.id === "__new__") {
          const title = draftTitle.trim();
          if (!title) {
            setError("Add a short title for this requirement.");
            setSaveState("Save failed");
            return;
          }
          patch.requirements = [
            ...(content.requirements || []),
            {
              id: `req:manual-${Date.now()}`,
              title,
              explanation: draftText.trim() || title,
              sourceLabel: "Added by you",
            },
          ];
        } else {
          patch.requirements = (content.requirements || []).map((item) =>
            item.id === editing.id ? { ...item, title: draftTitle || item.title, explanation: draftText } : item
          );
        }
      } else if (editing.section === "decisions") {
        if (editing.id === "__new__") {
          const selected = draftTitle.trim();
          if (!selected) {
            setError("Add the product decision engineering must follow.");
            setSaveState("Save failed");
            return;
          }
          patch.decisions = [
            ...(content.decisions || []),
            {
              id: `dec:manual-${Date.now()}`,
              selected,
              consequence: draftText.trim() || "Recorded by you.",
              confirmed: true,
            },
          ];
        } else {
          patch.decisions = (content.decisions || []).map((item) =>
            item.id === editing.id
              ? { ...item, selected: draftTitle || item.selected, consequence: draftText, confirmed: true }
              : item
          );
        }
      } else if (editing.section === "exclusions") {
        if (editing.id === "__new__") {
          const text = draftText.trim();
          if (!text) {
            setError("Say what is not included.");
            setSaveState("Save failed");
            return;
          }
          patch.exclusions = [
            ...(content.exclusions || []),
            { id: `exc:manual-${Date.now()}`, text, kind: "excluded" },
          ];
        } else {
          patch.exclusions = (content.exclusions || []).map((item) =>
            item.id === editing.id ? { ...item, text: draftText } : item
          );
        }
      } else if (editing.section === "successChecks") {
        if (editing.id === "__new__") {
          const text = draftText.trim();
          if (!text) {
            setError("Say how success will be checked.");
            setSaveState("Save failed");
            return;
          }
          patch.successChecks = [
            ...(content.successChecks || []),
            { id: `chk:manual-${Date.now()}`, text, kind: "human" },
          ];
        } else {
          patch.successChecks = (content.successChecks || []).map((item) =>
            item.id === editing.id ? { ...item, text: draftText } : item
          );
        }
      } else if (editing.section === "risks") {
        if (editing.id === "__new__") {
          const what = draftTitle.trim() || draftText.trim();
          if (!what) {
            setError("Describe the risk or deferred decision.");
            setSaveState("Save failed");
            return;
          }
          const kind = editing.mode === "deferral" || draftUsage === "deferral" ? "deferral" : "risk";
          patch.risks = [
            ...(content.risks || []),
            {
              id: `${kind === "deferral" ? "def" : "risk"}:manual-${Date.now()}`,
              kind,
              what,
              why: draftText.trim() || (kind === "deferral" ? "Intentionally deferred." : "Recorded by you."),
              owner: kind === "deferral" ? "product" : "engineering",
              consequence:
                kind === "deferral"
                  ? "Engineering should not invent this decision during implementation."
                  : "Engineering should treat this as residual risk.",
              revisitWhen: draftExtra.trim() || (kind === "deferral" ? "Before handoff" : "During review"),
              blocking: kind === "deferral" && /handoff|unsafe|must/i.test(`${draftText} ${draftExtra}`),
              provenance: "Added by you",
            },
          ];
        } else {
          patch.risks = (content.risks || []).map((item) =>
            item.id === editing.id
              ? { ...item, what: draftTitle || item.what, why: draftText || item.why, revisitWhen: draftExtra || item.revisitWhen }
              : item
          );
        }
      } else if (editing.section === "sources") {
        if (editing.id === "__new__") {
          const name = draftTitle.trim();
          if (!name) {
            setError("Give this source a recognizable name.");
            setSaveState("Save failed");
            return;
          }
          patch.sources = [
            ...(content.sources || []),
            {
              id: `src:manual-${Date.now()}`,
              name,
              type: /http/i.test(draftExtra) ? "linked_issue" : "note",
              contributed: draftText.trim() || "Manually added",
              confirmationStatus: "confirmed",
              usage: draftUsage || "context",
              href: draftExtra.trim() || undefined,
            },
          ];
        } else {
          patch.sources = (content.sources || []).map((item) =>
            item.id === editing.id
              ? {
                  ...item,
                  name: draftTitle || item.name,
                  contributed: draftText || item.contributed,
                  usage: draftUsage || item.usage,
                  href: draftExtra || item.href,
                }
              : item
          );
        }
      }
      const data = await patchTaskProductDocument(plan._id, {
        baseRevision: document.revision,
        content: patch,
      });
      setPayload(data);
      onDocumentChange?.(data);
      setEditing(null);
      setSaveState("Saved");
    } catch (err) {
      const code = err?.response?.data?.code;
      setSaveState(code === "STALE" ? "Conflict detected" : "Save failed");
      setError(err?.response?.data?.error || err?.message || "This change could not be saved.");
      if (code === "STALE") {
        setConflict({ title: draftTitle, text: draftText });
      }
    }
  };

  const startAdd = (section, mode) => {
    if (frozen || !mutateAllowed) return;
    setEditing({ section, id: "__new__", mode: mode || "add" });
    setDraftTitle("");
    setDraftText("");
    setDraftExtra("");
    setDraftUsage(section === "risks" && mode === "deferral" ? "deferral" : section === "sources" ? "context" : "risk");
  };

  const removeRequirement = async (item) => {
    if (frozen || !mutateAllowed) return;
    setSaveState("Saving");
    try {
      const data = await patchTaskProductDocument(plan._id, {
        baseRevision: document.revision,
        content: { requirements: (content.requirements || []).filter((req) => req.id !== item.id) },
      });
      setPayload(data);
      onDocumentChange?.(data);
      setSaveState("Saved");
    } catch (err) {
      setSaveState("Save failed");
      setError(err?.response?.data?.error || "That requirement could not be removed.");
    }
  };

  const removeDecision = async (item) => {
    if (frozen || !mutateAllowed) return;
    setSaveState("Saving");
    try {
      const data = await patchTaskProductDocument(plan._id, {
        baseRevision: document.revision,
        content: { decisions: (content.decisions || []).filter((decision) => decision.id !== item.id) },
      });
      setPayload(data);
      onDocumentChange?.(data);
      setSaveState("Saved");
    } catch (err) {
      setSaveState("Save failed");
      setError(err?.response?.data?.error || "That decision could not be removed.");
    }
  };

  const moveRequirement = async (index, direction) => {
    if (frozen || !mutateAllowed) return;
    const next = [...(content.requirements || [])];
    const swap = index + direction;
    if (swap < 0 || swap >= next.length) return;
    [next[index], next[swap]] = [next[swap], next[index]];
    const data = await patchTaskProductDocument(plan._id, {
      baseRevision: document.revision,
      content: { requirements: next },
    });
    setPayload(data);
    onDocumentChange?.(data);
  };

  const removeRisk = async (item) => {
    if (frozen || !mutateAllowed) return;
    setSaveState("Saving");
    try {
      await persistPatch({ risks: (content.risks || []).filter((risk) => risk.id !== item.id) });
    } catch (err) {
      setSaveState("Save failed");
      setError(err?.response?.data?.error || "That item could not be removed.");
    }
  };

  const resolveDeferral = async (item) => {
    if (frozen || !mutateAllowed) return;
    setSaveState("Saving");
    try {
      await persistPatch({
        risks: (content.risks || []).map((risk) =>
          risk.id === item.id ? { ...risk, resolved: true, blocking: false } : risk
        ),
      });
    } catch (err) {
      setSaveState("Save failed");
      setError(err?.response?.data?.error || "That decision could not be resolved.");
    }
  };

  const confirmRemoveSource = async () => {
    if (!pendingRemove?.item) return;
    setSaveState("Saving");
    try {
      const item = pendingRemove.item;
      await persistPatch({
        sources: (content.sources || []).filter((source) => source.id !== item.id),
        requirements: (content.requirements || []).map((req) =>
          (req.sourceIds || []).includes(item.id)
            ? { ...req, sourceIds: (req.sourceIds || []).filter((id) => id !== item.id), sourceLabel: "Unresolved source" }
            : req
        ),
      });
    } catch (err) {
      setSaveState("Save failed");
      setError(err?.response?.data?.error || "That source could not be removed.");
    }
  };

  const uploadSourceFile = async (files) => {
    if (!repo?._id || frozen || !mutateAllowed) return;
    for (const file of Array.from(files || [])) {
      try {
        const created = await createTaskSource({ repoId: repo._id, originalFilename: file.name });
        const bytes = await file.arrayBuffer();
        await uploadTaskSourceContent({ repoId: repo._id, artifactId: created.artifactId, bytes });
        await persistPatch({
          sources: [
            ...(content.sources || []),
            {
              id: `src:file-${created.artifactId || Date.now()}`,
              name: file.name,
              type: file.type.startsWith("image/") ? "screenshot" : "document",
              contributed: "Uploaded file",
              confirmationStatus: "confirmed",
              usage: "context",
            },
          ],
        });
      } catch (err) {
        setError(err?.response?.data?.error || "That file could not be added.");
      }
    }
  };

  const discuss = (section, item) => {
    const context = {
      taskId: plan?._id,
      version: document.revision,
      section,
      itemId: item?.id || null,
      existingValue: item || content,
    };
    if (typeof onDiscuss === "function") onDiscuss(context);
    if (typeof onOpenConversation === "function") onOpenConversation(context);
  };

  const applyProposal = async (proposalId) => {
    const data = await applyTaskProductProposal(plan._id, proposalId);
    setPayload(data);
    onDocumentChange?.(data);
  };

  const rejectProposal = async (proposalId) => {
    const data = await rejectTaskProductProposal(plan._id, proposalId);
    setPayload(data);
    onDocumentChange?.(data);
  };

  const undo = async () => {
    const data = await undoTaskProductDocument(plan._id);
    setPayload(data);
    onDocumentChange?.(data);
  };

  const createSuccessor = async () => {
    try {
      const data = await unfreezeTaskProductDocument(plan._id);
      setPayload(data);
      onDocumentChange?.(data);
      setSaveState("Saved");
      setError("");
    } catch (err) {
      setError(err?.response?.data?.error || "This Task could not be unfrozen for editing.");
    }
  };

  const reloadDocument = async () => {
    const unsaved = conflict;
    await load();
    setConflict(null);
    setSaveState("Saved");
    if (unsaved?.text) setDraftText(unsaved.text);
    if (unsaved?.title) setDraftTitle(unsaved.title);
  };

  const copyUnsaved = async () => {
    const text = [conflict?.title, conflict?.text].filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      setError("Copy the text from the editor before reloading.");
    }
  };

  const approve = async () => {
    try {
      const data = await approveTaskProductDocument(plan._id);
      setPayload(data);
      onDocumentChange?.(data);
      setConfirmHandoff(false);
      onApproved?.(data);
    } catch (err) {
      setError(err?.response?.data?.error || "This Task is not ready to send.");
    }
  };

  useEffect(() => {
    if (!discussContext?.utterance || !plan?._id) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await createTaskProductProposal(plan._id, discussContext);
        if (!cancelled) {
          setPayload((prev) => ({ ...(prev || {}), ...data, proposals: [data.proposal] }));
        }
      } catch {
        /* conversation page owns the proposal flow */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [discussContext?.utterance, plan?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const repoLabel = repo?.owner && repo?.name ? `${repo.owner}/${repo.name}` : "Repository";

  const canMutate = !frozen && mutateAllowed;

  const editor = useMemo(() => {
    if (!editing) return null;
    return (
      <div className="full-task-editor" data-testid="full-task-editor">
        {editing.section === "requirements" ||
        editing.section === "outcome" ||
        editing.section === "decisions" ||
        editing.section === "risks" ||
        editing.section === "sources" ? (
          <label>
            {editing.section === "risks"
              ? editing.mode === "deferral" || draftUsage === "deferral"
                ? "Decision being deferred"
                : "Risk"
              : editing.section === "sources"
                ? "Name"
                : editing.section === "decisions"
                  ? "Decision"
                  : "Short title"}
            <input value={draftTitle} onChange={(event) => setDraftTitle(event.target.value)} />
          </label>
        ) : null}
        <label>
          {editing.section === "outcome"
            ? "Outcome"
            : editing.section === "risks"
              ? "Reason or impact"
              : editing.section === "sources"
                ? "How this was used"
                : editing.section === "decisions"
                  ? "Consequence for engineering"
                  : "Details"}
          <textarea value={draftText} onChange={(event) => setDraftText(event.target.value)} />
        </label>
        {editing.section === "risks" ? (
          <label>
            {editing.mode === "deferral" || draftUsage === "deferral" ? "When it must be decided" : "Mitigation or revisit"}
            <input value={draftExtra} onChange={(event) => setDraftExtra(event.target.value)} />
          </label>
        ) : null}
        {editing.section === "sources" ? (
          <>
            <label>
              Link
              <input value={draftExtra} onChange={(event) => setDraftExtra(event.target.value)} placeholder="https://…" />
            </label>
            <label>
              How to treat this source
              <select value={draftUsage} onChange={(event) => setDraftUsage(event.target.value)}>
                <option value="requirement">Requirement</option>
                <option value="context">Context</option>
                <option value="example">Example</option>
              </select>
            </label>
          </>
        ) : null}
        <div className="full-task-editor-actions">
          <button type="button" className="full-task-primary" onClick={() => void saveEdit()}>
            Save
          </button>
          <button type="button" className="full-task-secondary" onClick={() => setEditing(null)}>
            Cancel
          </button>
          <button type="button" className="full-task-btn-quiet" onClick={() => discuss(editing.section, { id: editing.id })}>
            Ask AI about this
          </button>
        </div>
      </div>
    );
  }, [editing, draftText, draftTitle, draftExtra, draftUsage]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loadState === "error") {
    return (
      <article className="full-task-page" data-testid="full-task-page">
        <div className="full-task-shell">
          <section className="full-task-section" role="alert" data-testid="full-task-missing">
            <h2>This Task could not be loaded</h2>
            <p>{error || "It may have been archived, or you may not have access in this workspace."}</p>
          </section>
        </div>
      </article>
    );
  }

  if (loadState === "loading" && !payload) {
    return (
      <article className="full-task-page" data-testid="full-task-page" data-loading="true">
        <div className="full-task-shell">
          <p className="full-task-sub">Loading Task…</p>
        </div>
      </article>
    );
  }


  return (
    <article className="full-task-page" data-testid="full-task-page" data-loaded="true">
      <div className="full-task-live" aria-live="polite">{saveState}{error ? `. ${error}` : ""}</div>
      <div className="full-task-shell">
        <header className="full-task-top">
          <div>
            {typeof onOpenConversation === "function" ? (
              <button type="button" className="full-task-back" onClick={() => onOpenConversation()}>
                ← Back to Conversation
              </button>
            ) : null}
            <p className="full-task-eyebrow">Full Task</p>
            <h1>{content.title || taskShortTitle(plan?.featureRequestText || "")}</h1>
            <p className="full-task-sub">This is the complete agreement engineering will receive.</p>
            <p className="full-task-sub">{repoLabel}</p>
            <p className="full-task-save" data-testid="full-task-save-state">{saveState}</p>
            {frozen ? (
              <p className="full-task-sub">This Task has been sent to engineering. Unfreeze it to edit again, then re-approve when ready.</p>
            ) : null}
            <details>
              <summary>Details</summary>
              <p>Version {document.revision}</p>
              {history.length ? (
                <button type="button" className="full-task-btn-quiet" onClick={() => setHistoryOpen((open) => !open)}>
                  Review version history
                </button>
              ) : null}
            </details>
          </div>
          <span className="full-task-ready" data-testid="full-task-state">{stateLabel}</span>
        </header>

        <div className="full-task-guide">
          <div aria-hidden="true">✦</div>
          <div>
            <strong>AI has organized the conversation into one handoff.</strong>
            <p>Read the agreement below. Use Ask AI anywhere something does not look right.</p>
          </div>
        </div>

        {error ? <p className="full-task-conflict" role="alert">{error}</p> : null}
        {conflict || saveState === "Conflict detected" ? (
          <section className="full-task-section" role="alert" data-testid="full-task-conflict">
            <h3>This Task changed since you opened it.</h3>
            <p>Reload to see the latest version, or copy your unsaved text first.</p>
            <div className="full-task-dialog-actions">
              <button type="button" className="full-task-primary" onClick={() => void reloadDocument()}>
                Review changes
              </button>
              <button type="button" className="full-task-secondary" onClick={() => void reloadDocument()}>
                Reload
              </button>
              <button type="button" className="full-task-btn-quiet" onClick={() => void copyUnsaved()}>
                Copy my unsaved text
              </button>
            </div>
          </section>
        ) : null}

        {historyOpen ? (
          <section className="full-task-section" data-testid="full-task-history">
            <h3>Version history</h3>
            <ul>
              {history.map((item) => (
                <li key={`${item.revision}-${item.createdAt}`}>
                  Revision {item.revision} · {item.origin} · {item.createdAt}
                </li>
              ))}
            </ul>
            {canMutate ? (
              <button type="button" className="full-task-secondary" onClick={() => void undo()}>
                Undo latest change
              </button>
            ) : null}
          </section>
        ) : null}

        {proposals.map((proposal) => {
          const preview = proposalPreview(proposal);
          return (
            <section key={proposal.proposalId} className="full-task-section" data-testid="full-task-proposal">
              <h3>AI suggests updating the Task</h3>
              <div className="full-task-proposal-diff">
                <p>{preview.why}</p>
                <p><strong>Before</strong> {preview.before}</p>
                <p><strong>After</strong> {preview.after}</p>
              </div>
              {preview.implications.length ? (
                <ul>
                  {preview.implications.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
              <div className="full-task-dialog-actions">
                <button type="button" className="full-task-primary" onClick={() => void applyProposal(proposal.proposalId)}>
                  Apply change
                </button>
                <button type="button" className="full-task-secondary" onClick={() => void rejectProposal(proposal.proposalId)}>
                  Reject
                </button>
              </div>
            </section>
          );
        })}

        <div className="full-task-layout">
          <nav className="full-task-outline" aria-label="Full Task sections">
            {SECTIONS.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => window.document.getElementById(`full-task-${item.id}`)?.scrollIntoView({ behavior: "smooth" })}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="full-task-document">
            <section className="full-task-hero" id="full-task-outcome">
              <TaskSectionHeader
                title="Outcome"
                description="What success looks like for this Task"
                actions={[
                  ...(canMutate
                    ? [{ key: "edit", label: "Edit", onClick: () => startEdit("outcome") }]
                    : []),
                  { key: "ask", label: "Ask AI", onClick: () => discuss("outcome") },
                ]}
              />
              <h2 data-testid="full-task-outcome">{content.outcome || "Add the outcome engineering should deliver."}</h2>
              {content.outcomeExplanation ? <p>{content.outcomeExplanation}</p> : null}
              {editing?.section === "outcome" ? editor : null}
            </section>

            <section className="full-task-section" id="full-task-requirements">
              <TaskSectionHeader
                title="What engineering will build"
                description={`${materialRequirements.length} requirement${materialRequirements.length === 1 ? "" : "s"}`}
                actions={[
                  ...(canMutate
                    ? [{ key: "add", label: "Add", onClick: () => startAdd("requirements"), testId: "full-task-add-requirement" }]
                    : []),
                  { key: "ask", label: "Ask AI", onClick: () => discuss("requirements") },
                ]}
              />
              <div className="full-task-stack" data-testid="full-task-requirements">
                {materialRequirements.map((item, index) => (
                  <TaskItemRow
                    key={item.id}
                    testId="full-task-requirement"
                    marker={index + 1}
                    title={item.title}
                    body={item.explanation}
                    meta={item.sourceLabel}
                    menuLabel={`Actions for ${item.title}`}
                    menuItems={
                      canMutate
                        ? [
                            { key: "edit", label: "Edit", onClick: () => startEdit("requirements", item) },
                            { key: "ask", label: "Ask AI", onClick: () => discuss("requirements", item) },
                            { key: "up", label: "Move up", onClick: () => void moveRequirement(index, -1) },
                            { key: "down", label: "Move down", onClick: () => void moveRequirement(index, 1) },
                            { key: "remove", label: "Remove", danger: true, onClick: () => void removeRequirement(item) },
                          ]
                        : [{ key: "ask", label: "Ask AI", onClick: () => discuss("requirements", item) }]
                    }
                  >
                    {editing?.id === item.id ? editor : null}
                  </TaskItemRow>
                ))}
              </div>
              {unrelated.length ? (
                <div className="full-task-unrelated" data-testid="full-task-unrelated">
                  <h4>Possibly unrelated</h4>
                  {unrelated.map((item) => (
                    <p key={item.id}>{item.title}</p>
                  ))}
                </div>
              ) : null}
              {editing?.section === "requirements" && editing.id === "__new__" ? editor : null}
            </section>

            <section className="full-task-section" id="full-task-decisions">
              <TaskSectionHeader
                title="Product decisions"
                description="The choices that shaped this Task"
                actions={[
                  ...(canMutate
                    ? [{ key: "add", label: "Add", onClick: () => startAdd("decisions"), testId: "full-task-add-decision" }]
                    : []),
                  { key: "ask", label: "Ask AI", onClick: () => discuss("decisions") },
                ]}
              />
              {(content.decisions || []).length ? (
                <div className="full-task-decisions">
                  {(content.decisions || []).map((item) => (
                    <TaskItemRow
                      key={item.id}
                      className="is-decision"
                      testId="full-task-decision"
                      title={item.selected}
                      body={item.consequence}
                      meta={item.confirmed ? "Decided by you" : "Needs your answer"}
                      menuLabel={`Actions for ${item.selected}`}
                      menuItems={[
                        ...(canMutate
                          ? [
                              { key: "edit", label: "Edit", onClick: () => startEdit("decisions", item) },
                              { key: "ask", label: "Ask AI", onClick: () => discuss("decisions", item) },
                              {
                                key: "remove",
                                label: "Remove",
                                danger: true,
                                onClick: () => void removeDecision(item),
                              },
                            ]
                          : [{ key: "ask", label: "Ask AI", onClick: () => discuss("decisions", item) }]),
                      ]}
                    >
                      {editing?.id === item.id ? editor : null}
                    </TaskItemRow>
                  ))}
                </div>
              ) : (
                <EmptySectionState>No product decisions recorded yet.</EmptySectionState>
              )}
              {editing?.section === "decisions" && editing.id === "__new__" ? editor : null}
            </section>

            <section className="full-task-section" id="full-task-exclusions">
              <TaskSectionHeader
                title="What is not included"
                description="Boundaries prevent accidental scope growth"
                actions={[
                  ...(canMutate
                    ? [{ key: "add", label: "Add", onClick: () => startAdd("exclusions"), testId: "full-task-add-exclusion" }]
                    : []),
                  { key: "ask", label: "Ask AI", onClick: () => discuss("exclusions") },
                ]}
              />
              {(content.exclusions || []).length ? (
                <div className="full-task-stack">
                  {(content.exclusions || []).map((item) => (
                    <TaskItemRow
                      key={item.id}
                      className="is-exclusion"
                      testId="full-task-exclusion"
                      marker="—"
                      title={item.text}
                      menuLabel={`Actions for exclusion`}
                      menuItems={
                        canMutate
                          ? [
                              { key: "edit", label: "Edit", onClick: () => startEdit("exclusions", item) },
                              { key: "ask", label: "Ask AI", onClick: () => discuss("exclusions", item) },
                            ]
                          : [{ key: "ask", label: "Ask AI", onClick: () => discuss("exclusions", item) }]
                      }
                    >
                      {editing?.id === item.id ? editor : null}
                    </TaskItemRow>
                  ))}
                </div>
              ) : (
                <EmptySectionState>Nothing has been excluded yet.</EmptySectionState>
              )}
              {editing?.section === "exclusions" && editing.id === "__new__" ? editor : null}
            </section>

            <section className="full-task-section" id="full-task-successChecks">
              <TaskSectionHeader
                title="How success will be checked"
                description="Planned verification before approval — not results yet"
                actions={[
                  ...(canMutate
                    ? [
                        {
                          key: "add",
                          label: "Add",
                          onClick: () => startAdd("successChecks"),
                          testId: "full-task-add-success-check",
                        },
                      ]
                    : []),
                  { key: "ask", label: "Ask AI", onClick: () => discuss("successChecks") },
                ]}
              />
              {(content.successChecks || []).length ? (
                <div className="full-task-stack">
                  {(content.successChecks || []).map((item) => (
                    <TaskItemRow
                      key={item.id}
                      className="is-check"
                      testId="full-task-check"
                      marker="◯"
                      title={item.text}
                      badge={<EvidenceBadge kind={item.kind} />}
                      menuLabel={`Actions for success check`}
                      menuItems={
                        canMutate
                          ? [
                              { key: "edit", label: "Edit", onClick: () => startEdit("successChecks", item) },
                              { key: "ask", label: "Ask AI", onClick: () => discuss("successChecks", item) },
                            ]
                          : [{ key: "ask", label: "Ask AI", onClick: () => discuss("successChecks", item) }]
                      }
                    >
                      {editing?.id === item.id ? editor : null}
                    </TaskItemRow>
                  ))}
                </div>
              ) : (
                <EmptySectionState>No success checks yet.</EmptySectionState>
              )}
              {editing?.section === "successChecks" && editing.id === "__new__" ? editor : null}
            </section>

            <section className="full-task-section" id="full-task-risks">
              <TaskSectionHeader
                title="Risks and deferrals"
                description="Risks are things that could go wrong. Deferred decisions are intentionally left for later."
                actions={[
                  ...(canMutate
                    ? [
                        { key: "add-risk", label: "Add risk", onClick: () => startAdd("risks", "risk") },
                        {
                          key: "add-deferral",
                          label: "Add deferred decision",
                          onClick: () => startAdd("risks", "deferral"),
                          testId: "full-task-add-deferral",
                        },
                      ]
                    : []),
                  { key: "ask", label: "Ask AI", onClick: () => discuss("risks") },
                ]}
              />
              {!risks.length && !deferrals.length ? (
                <EmptySectionState testId="full-task-no-risks">No known risks or deferred decisions.</EmptySectionState>
              ) : null}
              {risks.length ? (
                <div className="full-task-subgroup" data-testid="full-task-risk-list">
                  <h4>Known risks</h4>
                  <div className="full-task-stack">
                    {risks.map((item) => (
                      <TaskItemRow
                        key={item.id}
                        id={`full-task-item-${item.id}`}
                        testId="full-task-risk"
                        title={item.what}
                        body={item.why}
                        meta={[item.impact ? `Impact: ${item.impact}` : null, item.mitigation ? `Mitigation: ${item.mitigation}` : null, item.provenance || "Recorded on this Task"]
                          .filter(Boolean)
                          .join(" · ")}
                        menuItems={
                          canMutate
                            ? [
                                { key: "edit", label: "Edit", onClick: () => startEdit("risks", item) },
                                { key: "ask", label: "Ask AI", onClick: () => discuss("risks", item) },
                                { key: "remove", label: "Remove", danger: true, onClick: () => void removeRisk(item) },
                              ]
                            : [{ key: "ask", label: "Ask AI", onClick: () => discuss("risks", item) }]
                        }
                      >
                        {editing?.id === item.id ? editor : null}
                      </TaskItemRow>
                    ))}
                  </div>
                </div>
              ) : null}
              {deferrals.length ? (
                <div className="full-task-subgroup" data-testid="full-task-deferral-list">
                  <h4>Deferred decisions</h4>
                  <div className="full-task-stack">
                    {deferrals.map((item) => (
                      <TaskItemRow
                        key={item.id}
                        id={`full-task-item-${item.id}`}
                        testId="full-task-deferral"
                        className={item.blocking ? "is-blocking" : ""}
                        title={item.what}
                        body={item.why}
                        meta={`Owner: ${item.owner || "Not set"} · Decide by: ${item.revisitWhen || "Not set"}`}
                        warning={
                          item.blocking
                            ? "This deferral makes the engineering handoff unsafe until it is settled."
                            : null
                        }
                        menuItems={
                          canMutate
                            ? [
                                { key: "resolve", label: "Resolve", onClick: () => void resolveDeferral(item) },
                                { key: "edit", label: "Edit", onClick: () => startEdit("risks", item, "deferral") },
                                { key: "ask", label: "Ask AI", onClick: () => discuss("risks", item) },
                                { key: "remove", label: "Remove", danger: true, onClick: () => void removeRisk(item) },
                              ]
                            : [{ key: "ask", label: "Ask AI", onClick: () => discuss("risks", item) }]
                        }
                      >
                        {editing?.id === item.id ? editor : null}
                      </TaskItemRow>
                    ))}
                  </div>
                </div>
              ) : null}
              {editing?.section === "risks" && editing.id === "__new__" ? editor : null}
            </section>

            <section className="full-task-section" id="full-task-sources">
              <TaskSectionHeader
                title="What this Task is based on"
                description="Sources that established requirements, provided context, or are only examples"
                actions={[{ key: "ask", label: "Ask AI", onClick: () => discuss("sources") }]}
              />
              <div className="full-task-stack" data-testid="full-task-sources">
                {(content.sources || []).map((item) => (
                  <SourceRow
                    key={item.id}
                    item={item}
                    open={sourceOpen === item.id}
                    canMutate={canMutate}
                    onToggleView={(source) => setSourceOpen(sourceOpen === source.id ? null : source.id)}
                    onEdit={(source) => startEdit("sources", source)}
                    onAskAi={(source) => discuss("sources", source)}
                    onRemove={(source) =>
                      setPendingRemove({
                        kind: "source",
                        item: source,
                        dependents: (content.requirements || []).filter(
                          (req) => (req.sourceIds || []).includes(source.id) || req.sourceLabel === source.name
                        ),
                      })
                    }
                  />
                ))}
              </div>
              {!(content.sources || []).length ? (
                <EmptySectionState>No sources recorded yet.</EmptySectionState>
              ) : null}
              {canMutate ? (
                <div className="full-task-section-actions">
                  <button type="button" className="full-task-secondary" onClick={() => startAdd("sources")}>
                    Add source
                  </button>
                  <button type="button" className="full-task-secondary" onClick={() => sourceFileRef.current?.click()}>
                    Upload file
                  </button>
                  <button type="button" className="full-task-secondary" onClick={() => startAdd("sources")}>
                    Add link
                  </button>
                  <input
                    ref={sourceFileRef}
                    type="file"
                    accept={TASK_SOURCE_ACCEPT}
                    hidden
                    onChange={(event) => {
                      void uploadSourceFile(event.target.files);
                      event.target.value = "";
                    }}
                  />
                </div>
              ) : null}
              {editing?.section === "sources" && editing.id === "__new__" ? editor : null}
              {pendingRemove?.kind === "source" ? (
                <div className="full-task-dialog" role="alertdialog" aria-labelledby="full-task-source-remove-title" data-testid="full-task-source-remove">
                  <h3 id="full-task-source-remove-title">Remove this source?</h3>
                  <p>Removing {sourceDisplayName(pendingRemove.item)} may leave these items without a recorded source:</p>
                  <ul>
                    {(pendingRemove.dependents || []).length ? (
                      pendingRemove.dependents.map((item) => <li key={item.id}>{item.title}</li>)
                    ) : (
                      <li>No requirements currently name this source.</li>
                    )}
                  </ul>
                  <div className="full-task-dialog-actions">
                    <button type="button" className="full-task-danger" onClick={() => void confirmRemoveSource()}>
                      Remove source
                    </button>
                    <button type="button" className="full-task-secondary" onClick={() => setPendingRemove(null)}>
                      Keep source
                    </button>
                  </div>
                </div>
              ) : null}
            </section>
          </div>
        </div>

        <footer className="full-task-actions">
          <div>
            {readiness.ready ? (
              <p>
                <strong>{readiness.requirementCount} requirements</strong> · {readiness.decisionCount} decisions ·{" "}
                {readiness.successCheckCount} success checks · No open product questions
              </p>
            ) : (
              <div data-testid="full-task-blockers">
                <p>
                  {(readiness.blockers || []).length === 1
                    ? "One thing still needs your attention"
                    : `${(readiness.blockers || []).length} things still need your attention`}
                </p>
                <ul>
                  {(readiness.blockers || []).map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
          <div className="full-task-section-actions">
            <button type="button" className="full-task-btn-quiet" onClick={() => discuss("requirements")}>
              Ask AI
            </button>
            {frozen && mutateAllowed ? (
              <button type="button" className="full-task-secondary" data-testid="full-task-unfreeze" onClick={() => void createSuccessor()}>
                Unfreeze to edit
              </button>
            ) : null}
            {!frozen && readiness.ready ? (
              <button type="button" className="full-task-primary" data-testid="full-task-handoff" onClick={() => setConfirmHandoff(true)}>
                Approve and send to engineering
              </button>
            ) : null}
          </div>
        </footer>

        {confirmHandoff ? (
          <section className="full-task-dialog" role="dialog" aria-labelledby="full-task-handoff-title" data-testid="full-task-handoff-confirm">
            <h3 id="full-task-handoff-title">Approve this Task?</h3>
            <p>Version {document.revision}. {content.outcome}</p>
            <p>{readiness.requirementCount} requirements. Remaining deferrals: {(content.risks || []).length}.</p>
            <p>Engineering will receive this Task and an AI-prepared Engineering Brief. You can unfreeze later if you need to edit again.</p>
            <div className="full-task-dialog-actions">
              <button type="button" className="full-task-primary" onClick={() => void approve()}>
                Approve and send to engineering
              </button>
              <button type="button" className="full-task-secondary" onClick={() => setConfirmHandoff(false)}>
                Cancel
              </button>
            </div>
          </section>
        ) : null}
      </div>
    </article>
  );
}
